import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeleteOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Card, Modal, Skeleton, Space, Tag, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RouteStatus } from "@/types/route/route.common";
// Components
import { companyTabPath } from "@/components/Company/companyTabs";
import { Filters } from "@/components/filters";
// Store
import { getRootStore } from "@/stores/RootStore";
// Types and Utils
import { getOrderStatusColor } from "@/utils/orderHelpers";
// Styles
import { DataTable } from "@/components/DataTable";
import styles from "@/styles/RefactoredOrdersPage.module.less";
import { OrderStatus } from "@/types/order/order.common";
import { formatDateAndTime } from "@/utils/dateAndTimeFormat";
import { B2B_ENABLED } from "@/utils/featureFlags";
import { ORDER_STATUS_LABELS } from "@/utils/orderHelpers";
// === Утилиты форматирования ===
/** Форматирует секунды в «Xч Yмин» или «Y мин» */
function formatSeconds(seconds) {
    if (seconds == null || seconds <= 0)
        return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0)
        return `${h}ч ${m}мин`;
    return `${m} мин`;
}
/** Русское склонение слова «зарядка» по количеству */
function pluralCharges(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11)
        return "зарядка";
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
        return "зарядки";
    return "зарядок";
}
/** Вычисляет оставшееся время зарядки в секундах */
function calcRemainingSeconds(estimatedChargingTimeSeconds, chargingStartAt) {
    if (estimatedChargingTimeSeconds == null)
        return null;
    if (!chargingStartAt)
        return estimatedChargingTimeSeconds;
    const elapsedSec = Math.floor((Date.now() - new Date(chargingStartAt).getTime()) / 1000);
    return estimatedChargingTimeSeconds - elapsedSec;
}
const RefactoredOrdersPage = observer(() => {
    const { orderStore } = getRootStore();
    const { dataTable, loading, filtersStore } = orderStore;
    const navigate = useNavigate();
    useEffect(() => {
        dataTable.setFilters(filtersStore.values);
        dataTable.loadData();
    }, [filtersStore.values]);
    // FR-007: компании для фильтра «Плательщик» (только при B2B_ENABLED — внутри стора)
    useEffect(() => {
        orderStore.loadPayerCompanies();
    }, []);
    // Очищаем авто-обновление при размонтировании страницы
    useEffect(() => {
        return () => {
            orderStore.dispose();
        };
    }, []);
    const handleViewOrder = (orderId) => {
        if (orderId) {
            navigate(`/orders/${orderId}`);
        }
        else {
            navigate("/orders/new");
        }
    };
    const handleDeleteOrder = (orderId) => {
        Modal.confirm({
            title: "Удалить заказ?",
            content: "Вы уверены, что хотите удалить этот заказ?",
            okText: "Удалить",
            okType: "danger",
            cancelText: "Отмена",
            onOk: () => orderStore.deleteOrder(orderId),
        });
    };
    // === TABLE CONFIGURATION ===
    // FR-007 (UC-101): кто платит. Рендер читает только поля строки — наблюдаемых стора здесь нет.
    // Строка без колонок плательщика считается частной (BR-9).
    const payerColumns = B2B_ENABLED
        ? [
            {
                title: "Плательщик",
                key: "payer",
                width: 160,
                render: (_, record) => (_jsx("span", { "data-testid": `corp-orders-row-${record.id}-payer`, children: !record.isCorporate ? ("Физлицо") : record.corpCompanyId ? (_jsx(Link, { to: companyTabPath(record.corpCompanyId), children: record.corpCompanyName ?? "Компания" })) : (record.corpCompanyName ?? "Компания") })),
            },
        ]
        : [];
    const columns = [
        {
            title: "Номер заказа",
            dataIndex: "orderNumber",
            key: "orderNumber",
            fixed: "left",
            width: 150,
            render: (text, record) => (_jsx(Button, { type: "link", onClick: () => handleViewOrder(record.id), className: styles.orderNumberLink, children: text })),
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => (_jsx(Tag, { color: getOrderStatusColor(status), children: ORDER_STATUS_LABELS[status] })),
        },
        {
            title: "Адрес",
            key: "addressInfo",
            width: 200,
            ellipsis: { showTitle: false },
            render: (_, record) => (_jsx(Tooltip, { title: record.addressInfo.getAddress(), children: record.addressInfo.getAddress() || _jsx("span", { className: styles.timeEmpty, children: "\u0410\u0434\u0440\u0435\u0441 \u043D\u0435 \u0443\u043A\u0430\u0437\u0430\u043D" }) })),
        },
        {
            title: "Клиент",
            key: "clientInfo",
            width: 180,
            render: (_, record) => {
                const { clientInfo } = record;
                if (!clientInfo?.firstName && !clientInfo?.lastName && !clientInfo?.phone) {
                    return _jsx("span", { className: styles.timeEmpty, children: "\u2014" });
                }
                const fullName = [clientInfo.firstName, clientInfo.lastName]
                    .filter(Boolean)
                    .join(" ");
                return (_jsxs("div", { children: [fullName && _jsx("div", { className: styles.clientName, children: fullName }), clientInfo.phone && (_jsx("div", { className: styles.clientPhone, children: clientInfo.phone })), clientInfo.completedOrdersCount != null && clientInfo.completedOrdersCount > 0 && (_jsxs("div", { className: styles.clientBadge, children: ["\u2713 ", clientInfo.completedOrdersCount] }))] }));
            },
        },
        ...payerColumns,
        {
            title: "Авто",
            key: "vehicleInfo",
            width: 180,
            render: (_, record) => {
                const { vehicleInfo } = record;
                const brand = vehicleInfo?.brand;
                const model = vehicleInfo?.model ?? vehicleInfo?.manufacturer;
                const stateNumber = vehicleInfo?.stateNumber;
                const dcPort = vehicleInfo?.dcPort ?? vehicleInfo?.connectorType;
                const battery = vehicleInfo?.batteryCapacity;
                if (!brand && !model && !stateNumber) {
                    return _jsx("span", { className: styles.timeEmpty, children: "\u2014" });
                }
                return (_jsxs("div", { children: [(brand || model) && (_jsx("div", { className: styles.vehicleName, children: [brand, model].filter(Boolean).join(" ") })), _jsxs("div", { className: styles.vehicleDetails, children: [dcPort && _jsx("span", { children: dcPort }), battery && _jsx("span", { children: battery && ` · ${battery} кВт·ч` })] }), stateNumber && (_jsx("div", { className: styles.vehicleStateNumber, children: stateNumber }))] }));
            },
        },
        {
            title: "Маршрут",
            key: "routeInfo",
            width: 150,
            render: (_, record) => {
                if (!record.routeInfo) {
                    return _jsx("span", { className: styles.timeEmpty, children: "\u2014" });
                }
                const { routeId, plannedStartTime, status } = record.routeInfo;
                const isActive = ![RouteStatus.COMPLETED, RouteStatus.CANCELLED].includes(status);
                return (_jsx(Tooltip, { title: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043C\u0430\u0440\u0448\u0440\u0443\u0442", children: _jsx(Button, { type: "link", style: { padding: 0, height: "auto", fontWeight: isActive ? 600 : 400 }, onClick: () => navigate(`/routes/${routeId}`), children: formatDateAndTime(plannedStartTime) }) }));
            },
        },
        {
            title: "Дата/Время",
            key: "scheduledDate",
            dataIndex: "scheduledDate",
            width: 140,
            render: (_, record) => (_jsx("div", { children: formatDateAndTime(record.scheduledDate) })),
            sorter: true,
        },
        {
            title: "По статистике",
            key: "historicalTime",
            width: 120,
            render: (_, record) => {
                const seconds = record.chargingStats?.historicalChargingTimeSeconds;
                const hasVehicle = !!record.vehicleInfo?.idVehicle;
                if (!hasVehicle) {
                    return _jsx("span", { className: styles.timeEmpty, children: "\u2014" });
                }
                const formatted = formatSeconds(seconds);
                if (!formatted) {
                    const count = record.chargingStats?.completedChargesCount ?? 0;
                    return (_jsx(Tooltip, { title: `Завершённых заказов с зарядной сессией: ${count}. Для расчёта статистики нужно ≥ 2.`, children: _jsxs("span", { className: styles.timeWarning, children: [_jsx(WarningOutlined, {}), " ", count, " ", pluralCharges(count)] }) }));
                }
                return _jsx("span", { className: styles.timeValue, children: formatted });
            },
        },
        {
            title: "По кривой",
            key: "plannedTime",
            width: 110,
            render: (_, record) => {
                const seconds = record.chargingStats?.plannedChargingTimeSeconds;
                const formatted = formatSeconds(seconds);
                if (!formatted)
                    return _jsx("span", { className: styles.timeEmpty, children: "\u2014" });
                return _jsx("span", { className: styles.timeValue, children: formatted });
            },
        },
        {
            title: "Осталось",
            key: "remainingTime",
            width: 100,
            render: (_, record) => {
                if (record.status !== OrderStatus.CHARGING) {
                    return _jsx("span", { className: styles.timeEmpty, children: "\u2014" });
                }
                const { estimatedChargingTimeSeconds, chargingStartAt } = record.chargingStats ?? {};
                const remaining = calcRemainingSeconds(estimatedChargingTimeSeconds, chargingStartAt);
                if (remaining == null)
                    return _jsx("span", { className: styles.timeEmpty, children: "\u2014" });
                if (remaining <= 0) {
                    return _jsx("span", { className: styles.timeRemainingEnding, children: "\u0417\u0430\u0432\u0435\u0440\u0448\u0430\u0435\u0442\u0441\u044F" });
                }
                const formatted = formatSeconds(remaining);
                return _jsx("span", { className: styles.timeRemaining, children: formatted });
            },
        },
        {
            title: "Действия",
            key: "actions",
            fixed: "right",
            width: 80,
            render: (_, record) => (_jsx(Space, { size: "small", children: _jsx(Tooltip, { title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C", children: _jsx(Button, { type: "text", danger: true, icon: _jsx(DeleteOutlined, {}), onClick: () => handleDeleteOrder(record.id) }) }) })),
        },
    ];
    const dataTableConfig = {
        header: {
            title: "Управление заказами",
            actions: (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => handleViewOrder(), children: "\u041D\u043E\u0432\u044B\u0439 \u0437\u0430\u043A\u0430\u0437" })),
            extraContent: (_jsx(Filters, { filtersStore: filtersStore, title: "\u0424\u0438\u043B\u044C\u0442\u0440\u044B \u0438 \u043F\u043E\u0438\u0441\u043A", showClearAll: true, showApplyButton: false })),
        },
        columns: columns,
        rowKey: "id",
        scroll: { x: B2B_ENABLED ? 1750 : 1590 },
    };
    if (loading) {
        return (_jsx(Card, { children: _jsx(Skeleton, { active: true, paragraph: { rows: 8 } }) }));
    }
    // === RENDER ===
    return (_jsx("div", { "data-testid": "corp-orders-table", children: _jsx(DataTable, { store: dataTable, config: dataTableConfig }) }));
});
RefactoredOrdersPage.displayName = "RefactoredOrdersPage";
export default RefactoredOrdersPage;
